import { PayableApproval } from './payable-approvals';
import { User } from './user';
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import moment from 'moment-timezone';
import { Vendor } from 'app/models/entities/vendors/vendor';
import { UploadedFile } from './uploaded-file';
import { InboundEmail } from './inbound-email';
import { UnifiedBankAccount } from './unified-bank-account';
import { Transaction } from './transactions/transaction';
import { PayableFulfillment } from './payable-fulfillment';
import Moment = moment.Moment;
import { PayableExpenseCategory } from './payable-expense-category';
import { ActivityLog } from './activity-log';

export class Payable
{
    id: number;
    amount: number;
    confirmedBy: number;
    confirmedByUser: User;
    confirmedAt: Moment;
    dueDate: Moment;
    invoiceDate: Moment;
    invoiceNumber: string;
    invoiceUploadedFileId: number;
    memo: string;
    ocrProcessedAt: Moment;
    organizationId: number;
    payableBillNumber: number;
    payableStatus: string;
    payableStatusAt: Moment;
    paymentAmount: number;
    paymentType: string;
    payoutStatus: string;
    purchaseOrder: string;
    scheduledPaymentDate: Moment;
    expectedDeliveryDate: Moment;
    paymentSentAt: Moment;
    deletedAt: Moment;
    terms: string;
    vendorId: number;
    payeeEin: string;
    payeeEmail: string;
    payeePhone: string;
    payeeName: string;
    payeeAddress1: string;
    payeeAddress2: string;
    payeeCity: string;
    payeeState: string;
    payeeZip: string;
    payeeCountry: string;
    createdAt: Moment;
    updatedAt: Moment;
    vendor: Vendor;
    source: string;
    invoiceDescription: string;
    statusDescription: string;
    isCancellable: boolean;
    isDeletable: boolean;
    isSatisfied: boolean;
    isOverpayment: boolean;
    isPartialPayment: boolean;
    outstandingInvoiceAmount: number;
    payableUnifiedBankAccountId: number;
    status: string;
    allowedTransitions: string[];
    account: UnifiedBankAccount;
    transactions: Transaction[];
    expenseCategories: PayableExpenseCategory[];
    currentFulfillment: PayableFulfillment;

    attachments: UploadedFile[] = [];
    inboundEmail: InboundEmail;
    total: number;
    activityHistory: ActivityLog[] = [];

    originalModel: Payable;

    approverIds: number[] = [];
    checkNumber: string;

    constructor(payable: any) {
        if (!payable.originalModel) {
            this.originalModel = payable;
        }

        if (payable.id) {
            this.id = payable.id;
        }
        this.amount = payable.amount || 0;
        if (payable.confirmedBy) {
            this.confirmedBy = payable.confirmedBy;
        }
        // OCR-carried fields are read camelCase-or-snake_case (SD-7586): the InvoiceOcrCompleted
        // broadcast delivers the raw snake_case Eloquent toArray() shape, while the getOcrStatus
        // poll is camelCased by the CamelCaseResponseKeys middleware. Any field newly added to the
        // OCR extraction (PayabliService::mapOcrResponseToInvoice) must be added to the dual-case
        // set here too, or it will silently drop when the broadcast wins the race over the poll.
        const invoiceNumber = payable.invoiceNumber ?? payable.invoice_number;
        if (invoiceNumber) {
            this.invoiceNumber = invoiceNumber;
        } else if (Payable.hasKey(payable, 'invoiceNumber', 'invoice_number')) {
            // SD-8418: a key that is present but empty ('' or null) is an explicit clear (the Review-step
            // preview echoes the cleared form value back). It must be kept as null — not left
            // undefined — so the confirm payload built from `{ ...localPayable }` still carries the
            // key and the backend fill() blanks the draft's OCR-populated value instead of keeping it.
            this.invoiceNumber = null;
        }
        if (payable.invoiceUploadedFileId) {
            this.invoiceUploadedFileId = payable.invoiceUploadedFileId;
        }
        if (payable.memo) {
            this.memo = payable.memo;
        } else if (Payable.hasKey(payable, 'memo')) {
            // SD-8418: same explicit-clear handling as invoiceNumber (memo is also OCR-populated).
            this.memo = null;
        }
        if (payable.organizationId) {
            this.organizationId = payable.organizationId;
        }
        if (payable.payableBillNumber) {
            this.payableBillNumber = payable.payableBillNumber;
        }
        if (payable.payableStatus) {
            this.payableStatus = payable.payableStatus;
        }
        if (payable.paymentAmount) {
            this.paymentAmount = payable.paymentAmount;
        } else if (payable.amount) {
            // Temporarily set payment amount = amount until Payable can support partial/over payments
            this.paymentAmount = payable.amount;
        }
        if (payable.paymentType) {
            this.paymentType = payable.paymentType;
        }
        if (payable.purchaseOrder) {
            this.purchaseOrder = payable.purchaseOrder;
        }
        if (payable.terms) {
            this.terms = payable.terms;
        }
        const vendorId = payable.vendorId ?? payable.vendor_id;
        if (vendorId) {
            this.vendorId = vendorId;
        }
        if (payable.payeeEin) {
            this.payeeEin = payable.payeeEin;
        }
        if (payable.payeeEmail) {
            this.payeeEmail = payable.payeeEmail;
        }
        if (payable.payeePhone) {
            this.payeePhone = payable.payeePhone;
        }
        if (payable.payeeName) {
            this.payeeName = payable.payeeName;
        }
        if (payable.payeeAddress1) {
            this.payeeAddress1 = payable.payeeAddress1;
        }
        if (payable.payeeAddress2) {
            this.payeeAddress2 = payable.payeeAddress2;
        }
        if (payable.payeeCity) {
            this.payeeCity = payable.payeeCity;
        }
        if (payable.payeeState) {
            this.payeeState = payable.payeeState;
        }
        if (payable.payeeZip) {
            this.payeeZip = payable.payeeZip;
        }
        if (payable.payeeCountry) {
            this.payeeCountry = payable.payeeCountry;
        }
        if (payable.invoiceDescription) {
            this.invoiceDescription = payable.invoiceDescription;
        }
        if (payable.statusDescription) {
            this.statusDescription = payable.statusDescription;
        }
        if (payable.isCancellable) {
            this.isCancellable = payable.isCancellable;
        }
        if (payable.isDeletable) {
            this.isDeletable = payable.isDeletable;
        }
        if (payable.isSatisfied) {
            this.isSatisfied = payable.isSatisfied;
        }
        if (payable.isOverpayment) {
            this.isOverpayment = payable.isOverpayment;
        }
        if (payable.isPartialPayment) {
            this.isPartialPayment = payable.isPartialPayment;
        }
        if (payable.outstandingInvoiceAmount) {
            this.outstandingInvoiceAmount = payable.outstandingInvoiceAmount || 0;
        }
        if (payable.expenseCategories) {
            this.expenseCategories = payable.expenseCategories;
        }
        if (payable.invoiceUploadedFileId) {
            this.invoiceUploadedFileId = payable.invoiceUploadedFileId;
        }
        if (payable.payableUnifiedBankAccountId) {
            this.payableUnifiedBankAccountId = payable.payableUnifiedBankAccountId;
        }

        // timestamps
        const timezone = LegFiJwtService.getTimezone();
        if (payable.createdAt) {
            this.createdAt = moment.utc(payable.createdAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (payable.confirmedAt) {
            this.confirmedAt = moment.utc(payable.confirmedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (payable.updatedAt) {
            this.updatedAt = moment.utc(payable.updatedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        const ocrProcessedAt = payable.ocrProcessedAt ?? payable.ocr_processed_at;
        if (ocrProcessedAt) {
            this.ocrProcessedAt = moment.utc(ocrProcessedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        const dueDate = payable.dueDate ?? payable.due_date;
        if (dueDate) {
            this.dueDate = moment(dueDate, 'YYYY-MM-DD');
        }
        const invoiceDate = payable.invoiceDate ?? payable.invoice_date;
        if (invoiceDate) {
            this.invoiceDate = moment(invoiceDate, 'YYYY-MM-DD');
        }
        if (payable.payableStatusAt) {
            this.payableStatusAt = moment(payable.payableStatusAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (payable.scheduledPaymentDate) {
            this.scheduledPaymentDate = moment(payable.scheduledPaymentDate, 'YYYY-MM-DD');
        }
        if (payable.expectedDeliveryDate) {
            this.expectedDeliveryDate = moment(payable.expectedDeliveryDate);
        }
        if (payable.paymentSentAt) {
            this.paymentSentAt = moment(payable.paymentSentAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (payable.deletedAt) {
            this.deletedAt = moment.utc(payable.deletedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (payable.status) {
            this.status = payable.status;
        }
        if (payable.allowedTransitions) {
            this.allowedTransitions = payable.allowedTransitions;
        }
        if (payable.source) {
            this.source = payable.source;
        }

        // relationships
        if (payable.attachments) {
            this.attachments = payable.attachments.map((attachment) => new UploadedFile(attachment));
        } else {
            this.attachments = [];
        }
        if (payable.confirmedByUser) {
            this.confirmedByUser = new User(payable.confirmedByUser);
        }
        if (payable.vendor) {
            this.vendor = new Vendor(payable.vendor);
        }
        if (payable.inboundEmail) {
            this.inboundEmail = new InboundEmail(payable.inboundEmail);
        }
        if (payable.account) {
            this.account = new UnifiedBankAccount(payable.account);
        }
        if (payable.transactions) {
            this.transactions = payable.transactions.map((transaction) => new Transaction(transaction));
        }
        if (payable.currentFulfillment) {
            this.currentFulfillment = new PayableFulfillment(payable.currentFulfillment);

            if (this.currentFulfillment?.approvals) {
                this.approverIds = this.currentFulfillment.approvals
                    .filter((approval) => approval.approved || approval.denied)
                    .map(approval => approval.payableApproverId);
            }

            if (this.currentFulfillment?.fulfillmentReference?.checkNumber) {
                this.checkNumber = '#' + this.currentFulfillment.fulfillmentReference.checkNumber;
            }
        }
        if (payable.originalModel) {
            this.originalModel = payable.originalModel;
        }
        if (payable.activityHistory) {
            this.activityHistory = payable.activityHistory.map((activity: any) => new ActivityLog(activity));
        }
        this.payoutStatus = payable.payoutStatus;
        this.total = payable.total;
    }

    get approvalsRequired() {
        return this.currentFulfillment?.approvalRule?.numberRequired ?? 0;
    }

    get approvedCount() {
        return this.currentFulfillment?.approvedApprovalsCount || 0;
    }

    // Determine if user can approve or deny this payable (selected from available approvals in the current fullfillment)
    canApproveOrDenyPayable(payableApproverId: number) {
        if (!payableApproverId) {
            return false;
        }

        return this.approverIds.length === 0 || !(this.approverIds.length > 0 && this.approverIds.includes(payableApproverId));
    }

    /**
     * True when any of the given keys is present on the raw payload with a non-undefined value
     * (null and '' count as present: they are an explicit clear). An `undefined` value is treated as
     * absent, so re-wrapping a Payable instance whose field was never assigned does not turn into an
     * explicit null — regardless of whether tsconfig `useDefineForClassFields` initialises declared
     * fields as own properties.
     */
    private static hasKey(payable: any, ...keys: string[]): boolean {
        return !!payable && typeof payable === 'object' && keys.some(key => key in payable && payable[key] !== undefined);
    }
}
